"use client"

import { useState, useEffect, useRef } from "react"
import { useSearchParams } from "next/navigation"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { motion, AnimatePresence } from "framer-motion"
import { Eye, EyeOff, Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"
import { Icon } from "@/shared/components/icons"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"

import { registerSchema, type RegisterInput } from "@/features/auth/validations"
import { signUp } from "@/features/auth/actions"
import { collectBrowserFirstTouch } from "@/features/auth/lib/profile-first-touch"
import { AuthCharacterPanel } from "@/features/auth/components/auth-character-panel"
import { EmailVerificationResend } from "@/features/auth/components/email-verification-resend"
import { PasswordStrengthIndicator } from "@/features/auth/components/password-strength-indicator"
import { createClient } from "@/shared/lib/supabase/client"
import { ROUTES, getAuthCallbackUrl } from "@/shared/lib/constants"
import { cn } from "@/shared/lib/utils"
import { track } from "@/shared/lib/analytics"
import {
  DELETED_ACCOUNT_MESSAGE,
  isCorrectableEmailRejection,
} from "@/features/auth/lib/signup-error-message"
import { blockedDomainSignupCode, signupFailureCode } from "@/features/auth/lib/signup-failure-code"
import { useCharacter } from "@/shared/hooks/use-character"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card"
import { Separator } from "@/shared/components/ui/separator"

export function RegisterContent() {
  const t = useTranslations("auth.register")
  const auth = useTranslations("auth.shared")
  const errorsT = useTranslations("errors")
  const forms = useTranslations("forms")
  const navigation = useTranslations("chrome.nav")
  const searchParams = useSearchParams()
  const characterId = searchParams.get("characterId") ?? ""
  const redirectParam = searchParams.get("redirect")
  const redirectTo =
    redirectParam?.startsWith("/") && !redirectParam.startsWith("//")
      ? redirectParam
      : "/"
  const { data: character } = useCharacter(characterId)
  const showCharacter = !!character?.avatar
  const loginParams = new URLSearchParams()
  if (characterId) loginParams.set("characterId", characterId)
  if (redirectTo !== "/") loginParams.set("redirect", redirectTo)
  const loginQuery = loginParams.toString()
  const loginHref = loginQuery ? `${ROUTES.login}?${loginQuery}` : ROUTES.login
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const hasTrackedEmailFocus = useRef(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    formState: { errors },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    mode: "onBlur",
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const watchedPassword = watch("password")
  const emailField = register("email")

  useEffect(() => {
    track("auth:signup_start", { method: "email" })
  }, [])

  function handleEmailFocus() {
    if (hasTrackedEmailFocus.current) return

    hasTrackedEmailFocus.current = true
    track("auth:signup_field_focused", { field: "email" })
  }

  function onBlockedDomain(formErrors: { email?: { message?: string } }) {
    const errorCode = blockedDomainSignupCode(formErrors.email?.message)
    if (!errorCode) return
    track("auth:signup_fail", { method: "email", error_code: errorCode })
  }

  async function onSubmit(data: RegisterInput) {
    setIsLoading(true)
    setServerError(null)
    track("auth:signup_submit", { method: "email" })

    try {
      const result = await signUp({
        email: data.email,
        password: data.password,
        redirectTo,
        attribution: collectBrowserFirstTouch(),
      })

      setIsLoading(false)

      if (result.error) {
        track("auth:signup_fail", {
          method: "email",
          error_code: signupFailureCode({ message: result.error }),
        })
        setServerError(result.error)
        if (isCorrectableEmailRejection(result.error)) {
          setFocus("email")
        }
        return
      }

      setVerifiedEmail(data.email)
      setIsSuccess(true)
    } catch {
      track("auth:signup_fail", { method: "email", error_code: "unknown" })
      setServerError(errorsT("generic"))
      setIsLoading(false)
    }
  }

  async function handleGoogleSignIn() {
    setIsLoading(true)
    setServerError(null)
    track("auth:signup_start", { method: "google" })
    const supabase = createClient()
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        // intent=signup so the /api/auth/callback route can route any
        // post-exchange failure to auth:signup_fail (vs auth:login_fail).
        redirectTo: `${getAuthCallbackUrl()}?${new URLSearchParams({
          intent: "signup",
          next: redirectTo,
        }).toString()}`,
      },
    })
    if (error) {
      track("auth:signup_fail", {
        method: "google",
        error_code: signupFailureCode({ message: error.message }),
      })
      setServerError(error.message)
      setIsLoading(false)
    }
  }

  return (
    <div
      className={cn(
        "mx-auto w-full",
        showCharacter
          ? "grid max-w-4xl overflow-hidden rounded-2xl border border-border/50 sm:grid-cols-[0.95fr_1fr]"
          : "max-w-[335px] sm:max-w-[390px]"
      )}
    >
      <AuthCharacterPanel
        characterAvatar={character?.avatar}
        characterName={character?.name}
        className="sm:border-r sm:border-white/[0.08]"
      />
      <Card className="overflow-hidden rounded-[1.35rem] border-white/[0.12] bg-[radial-gradient(circle_at_30%_0%,rgba(29,125,242,0.18),transparent_36%),linear-gradient(145deg,#171218_0%,#0d1118_54%,#09090c_100%)] shadow-[0_24px_90px_rgba(0,0,0,0.52),inset_0_1px_0_rgba(255,255,255,0.08)] sm:rounded-[1.55rem]">
        <AnimatePresence mode="wait">
          {!isSuccess ? (
            <motion.div
              key="form"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <CardHeader className="px-4 pt-5 text-center sm:px-8 sm:pt-8">
                <CardTitle className="text-xl font-semibold text-white sm:text-[22px]">{auth("createAccount")}</CardTitle>
                <CardDescription className="mt-1 text-sm text-white/60">
                  {t("subtitle")}
                </CardDescription>
              </CardHeader>

              <CardContent className="px-4 pb-2 sm:px-8">
                {serverError && (
                  <div
                    className={cn(
                      "mb-4 rounded-lg border p-3",
                      isCorrectableEmailRejection(serverError)
                        ? "border-amber-400/30 bg-amber-400/[0.06]"
                        : "border-destructive/30 bg-destructive/5"
                    )}
                  >
                    <p
                      className={cn(
                        "text-sm",
                        isCorrectableEmailRejection(serverError) ? "text-amber-200" : "text-destructive"
                      )}
                    >
                      {serverError}
                    </p>
                    {serverError === DELETED_ACCOUNT_MESSAGE && (
                      <LocalizedLink
                        href={ROUTES.support}
                        className="mt-2 inline-block text-sm font-medium text-foreground underline underline-offset-4 transition-colors hover:text-foreground/80"
                      >
                        {navigation("support")}
                      </LocalizedLink>
                    )}
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit, onBlockedDomain)} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-[13px] text-white/80">{auth("email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={forms("emailPlaceholder")}
                      autoComplete="email"
                      disabled={isLoading}
                      {...emailField}
                      onChange={(event) => {
                        emailField.onChange(event)
                        setServerError(null)
                      }}
                      onFocus={handleEmailFocus}
                      className={cn(
                        "h-10 rounded-xl border-white/[0.08] bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:border-white/20 focus-visible:ring-white/30 sm:h-11",
                        errors.email && "border-red-500/50 focus-visible:ring-red-500/30"
                      )}
                    />
                    {errors.email && (
                      <p className="text-xs text-destructive">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-[13px] text-white/80">{auth("password")}</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={forms("newPasswordPlaceholder")}
                        autoComplete="new-password"
                        disabled={isLoading}
                        {...register("password")}
                        className={cn(
                          "h-10 rounded-xl border-white/[0.08] bg-white/[0.04] pr-10 text-white placeholder:text-white/40 focus-visible:border-white/20 focus-visible:ring-white/30 sm:h-11",
                          errors.password && "border-red-500/50 focus-visible:ring-red-500/30"
                        )}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <PasswordStrengthIndicator password={watchedPassword} />
                    {errors.password && (
                      <p className="text-xs text-destructive">{errors.password.message}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="h-11 w-full rounded-xl bg-white text-sm font-medium text-black transition-all hover:bg-white/90 active:scale-[0.98] sm:h-12 sm:text-[15px]"
                    size="lg"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t("creating")}
                      </>
                    ) : (
                      auth("createAccount")
                    )}
                  </Button>
                </form>

                <div className="relative my-5">
                  <Separator className="bg-white/[0.08]" />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#10131b] px-3 text-xs text-white/40">
                    {auth("orContinueWith")}
                  </span>
                </div>

                <Button
                  variant="outline"
                  type="button"
                  disabled={isLoading}
                  onClick={handleGoogleSignIn}
                  className="h-11 w-full rounded-xl border-white/[0.08] bg-white text-sm font-medium text-[#1f1f1f] transition-all hover:bg-white/90 active:scale-[0.98] sm:h-12 sm:text-[15px]"
                >
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  {auth("continueWithGoogle")}
                </Button>
              </CardContent>

              <CardFooter className="justify-center px-4 pb-5 pt-2 sm:px-8 sm:pb-7">
                {!isCorrectableEmailRejection(serverError) && (
                  <p className="text-sm text-white/60">
                    {t("alreadyHaveAccount")}{" "}
                    <LocalizedLink
                      href={loginHref}
                      className="font-medium text-foreground hover:text-foreground/80 transition-colors"
                    >
                      {auth("signIn")}
                    </LocalizedLink>
                  </p>
                )}
              </CardFooter>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/10">
                  <Icon name="message" size={32} className="h-8 w-8 text-foreground" />
                </div>
                <CardTitle className="text-2xl font-bold">
                  {auth("checkEmail")}
                </CardTitle>
                <CardDescription className="text-left">
                  {t("checkEmailDescription", { email: verifiedEmail ?? "" })}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3">
                  <Icon name="tick-circle" size={16} className="h-4 w-4 shrink-0 text-emerald-400" />
                  <p className="text-xs text-muted-foreground">
                    {auth("spamHint")}
                  </p>
                </div>

                <EmailVerificationResend
                  email={verifiedEmail ?? ""}
                  surface="page"
                  context="signup"
                  onConfirmed={() => window.location.assign(redirectTo)}
                />

                <div className="flex flex-col items-center gap-2 text-center">
                  <button
                    type="button"
                    onClick={() => setIsSuccess(false)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t("wrongEmailSignup")}
                  </button>
                  <LocalizedLink
                    href={loginHref}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-foreground/70 transition-colors"
                  >
                    {t("goToSignIn")}
                  </LocalizedLink>
                </div>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </div>
  )
}
